import { gtmSendEvent } from "@wojo/analytics";
import { useI18n } from "@wojo/localization";
import { DepartureHit } from "@wojo/services";
import { StoryblokImage } from "@wojo/storyblok";
import { Markdown, Modal, ModalBody, Text } from "@wojo/ui";
import React, { useState } from "react";
import { FiInfo } from "react-icons/fi";
import { TourClassLevelContent } from "../../../../generated/graphql";
import styles from "./TripBuilderDeparturesStep.module.scss";

type DepartureListClassModalProps = {
    departures: DepartureHit[];
};

export const DepartureListClassModal: React.FC<
    DepartureListClassModalProps
> = ({ departures }) => {
    const [isOpen, setIsOpen] = useState(false);
    const { productPage } = useI18n("productPage");

    const title = productPage.tripBuilder.departures.modalTriggerLabel.l({
        subject: productPage.tripBuilder.departures.columnClassLevel.l(),
    });

    const classes = departures.reduce((acc, departure) => {
        if (
            departure.classLevel &&
            !acc.some(
                (level) =>
                    departure.classLevel?.classLevel === level.classLevel,
            )
        ) {
            acc.push(departure.classLevel);
        }
        return acc;
    }, new Array<TourClassLevelContent>());

    if (classes.length === 0) {
        return null;
    }

    return (
        <>
            <button
                className={styles["info-button"]}
                type="button"
                onClick={() => {
                    gtmSendEvent({
                        event: "track",
                        ga4Tracking: {
                            ga4_object: "TRIP_BUILDER",
                            ga4_action: "MODAL_OPENED",
                            modalId: "Trip Type Info",
                        },
                    });
                    setIsOpen(true);
                }}
            >
                <FiInfo size={16} title={title} aria-label={title} />
            </button>
            <Modal
                onCloseRequested={() => {
                    gtmSendEvent({
                        event: "track",
                        ga4Tracking: {
                            ga4_object: "TRIP_BUILDER",
                            ga4_action: "MODAL_CLOSED",
                            modalId: "Trip Type Info",
                        },
                    });
                    setIsOpen(false);
                }}
                isOpen={isOpen}
                accessibleTitle={title}
            >
                <ModalBody>
                    <Text.Heading
                        order="4"
                        tag="h2"
                        style={{ marginBottom: "var(--g-spacing-sm)" }}
                    >
                        {productPage.tripBuilder.departures.columnClassLevel.l()}
                    </Text.Heading>

                    {classes.map((classType) => (
                        <React.Fragment key={classType.name}>
                            <Text.Heading
                                order="5"
                                tag="h3"
                                style={{
                                    marginTop: "var(--g-spacing-md)",
                                    marginBottom: "var(--g-spacing-sm)",
                                    display: "flex",
                                    gap: "var(--g-spacing-xs)",
                                }}
                            >
                                {!!classType.icon && (
                                    <StoryblokImage
                                        mobile={{
                                            asset: classType.icon,
                                        }}
                                        aria-hidden
                                        style={{
                                            height: "1lh",
                                            display: "flex",
                                        }}
                                    />
                                )}
                                {classType.name}
                            </Text.Heading>
                            {classType.description && (
                                <Markdown markdown={classType.description} />
                            )}
                        </React.Fragment>
                    ))}
                </ModalBody>
            </Modal>
        </>
    );
};
